import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { CensoredText, findProfaneWords } from "@/lib/profanityFilter";
import { checkCommentQuality } from "@/lib/gibberishFilter";
import { logRejection } from "@/lib/logRejection";

interface ReviewCommentsProps {
  reviewId: string;
  isReviewOwner?: boolean;
  currentUserId: string | null;
  isAdmin?: boolean;
  defaultOpen?: boolean;
  highlightCommentId?: string | null;
}

interface CommentRow {
  id: string;
  is_owner: boolean;
  content: string;
  created_at: string;
}

const ReviewComments = ({ reviewId, isReviewOwner, currentUserId, isAdmin, defaultOpen, highlightCommentId }: ReviewCommentsProps) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [count, setCount] = useState(0);

  const isOwner = !!isReviewOwner;
  const canSeeIdentities = isOwner || !!isAdmin;

  const fetchCount = async () => {
    const { count: c } = await supabase
      .from("review_comments_public" as any)
      .select("*", { count: "exact", head: true })
      .eq("review_id", reviewId);
    setCount(c || 0);
  };

  const fetchComments = async () => {
    const { data, error } = await supabase
      .from("review_comments_public" as any)
      .select("id, is_owner, content, created_at")
      .eq("review_id", reviewId)
      .order("created_at", { ascending: true });
    if (error) return;
    const rows = (data || []) as any as CommentRow[];
    setComments(rows);
    setCount(rows.length);
  };

  useEffect(() => {
    fetchCount();
  }, [reviewId]);

  useEffect(() => {
    if (open) fetchComments();
  }, [open, canSeeIdentities]);

  useEffect(() => {
    if (!highlightCommentId || !open || comments.length === 0) return;
    const el = document.getElementById(`comment-${highlightCommentId}`);
    if (el) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      return () => clearTimeout(t);
    }
  }, [highlightCommentId, open, comments]);

  const handleSubmit = async () => {
    if (!currentUserId) {
      toast({ title: "Please sign in to comment", variant: "destructive" });
      return;
    }
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.length > 1000) {
      toast({ title: "Comment too long", description: "Max 1,000 characters.", variant: "destructive" });
      return;
    }
    const quality = checkCommentQuality(trimmed);
    if (!quality.ok) {
      toast({
        title: "Please improve your comment",
        description: quality.reason,
        variant: "destructive",
      });
      logRejection({ kind: "comment", content: trimmed, reviewId, reason: quality.reason || "quality" });
      return;
    }
    const offending = findProfaneWords(trimmed);
    if (offending.length > 0) {
      toast({
        title: "Please adjust your language",
        description: `Your comment contains vulgar or offensive language (${offending.slice(0, 3).join(", ")}${offending.length > 3 ? "…" : ""}). Please rephrase before posting.`,
        variant: "destructive",
      });
      logRejection({ kind: "comment", content: trimmed, reviewId, reason: "profanity" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase
      .from("review_comments" as any)
      .insert({ review_id: reviewId, user_id: currentUserId, content: trimmed } as any);
    setSubmitting(false);
    if (error) {
      const msg = error.message || "";
      const isRate = /wait before posting/i.test(msg);
      toast({
        title: isRate ? "Please slow down" : "Failed to post comment",
        description: isRate ? "Please wait a moment before posting another comment." : msg,
        variant: "destructive",
      });
      logRejection({ kind: "comment", content: trimmed, reviewId, reason: msg });
      return;
    }
    setText("");
    fetchComments();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("review_comments" as any).delete().eq("id", id);
    if (error) {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
      return;
    }
    fetchComments();
  };

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        {open ? "Hide" : "Show"} Comments ({count})
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 space-y-3">
              {comments.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No comments yet.</p>
              )}
              {comments.map((c) => {
                const canDelete = isAdmin || c.is_owner;
                const label = "Anonymous";
                return (
                  <div
                    key={c.id}
                    id={`comment-${c.id}`}
                    className={`rounded-lg p-3 transition-colors ${
                      highlightCommentId === c.id
                        ? "bg-primary/10 ring-2 ring-primary/40"
                        : "bg-muted/40"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-semibold text-foreground">
                        {label}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(c.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap break-words"><CensoredText text={c.content} /></p>
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="mt-1 flex items-center gap-1 text-[10px] text-destructive hover:text-destructive/80"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                  </div>
                );
              })}

              {currentUserId ? (
                <div className="space-y-2">
                  <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Add a comment..."
                    rows={2}
                    maxLength={1000}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">{text.length}/1000</span>
                    <Button size="sm" onClick={handleSubmit} disabled={submitting || !text.trim()}>
                      {submitting ? "Posting..." : "Post comment"}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">Sign in to comment.</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReviewComments;
